"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * FAQ search — PROGRESSIVE ENHANCEMENT over the server-rendered /faq page.
 *
 * ⚠️ READ THIS BEFORE REFACTORING IT INTO A NORMAL REACT LIST. The obvious implementation is to
 * pass the FAQ array into a client component and render `items.filter(...)`. That would silently
 * destroy the reason /faq exists: the page is a SERVER component so its answers sit in the HTML
 * for crawlers that never execute JavaScript (measured — the client-rendered home page serves
 * 7 KB of "Loading…" to the same request). Rendering the list here would put all 41 answers behind
 * a hook and hand those crawlers an empty box.
 *
 * So this component renders ONLY the input, and filters by toggling `hidden` on nodes the server
 * already rendered. Three consequences worth knowing:
 *
 *   1. THE INDEX IS BUILT FROM THE DOM, not from props. `content/faq.ts` is never imported here.
 *      That costs zero bytes of payload (the alternative duplicates ~40 KB of answer text into the
 *      flight data) and it cannot drift from what is on screen, because it IS what is on screen.
 *   2. `hidden` is set imperatively and is never a JSX prop on those nodes, so React has no
 *      competing opinion about it. Server-component output does not re-render on the client, so
 *      nothing overwrites the toggles.
 *   3. THE INPUT IS WITHHELD UNTIL MOUNT, and that needs saying because the obvious assumption is
 *      wrong: "use client" does NOT mean "browser only". Next server-renders a client component's
 *      initial markup too, so without the `mounted` gate below the box appears in the static HTML
 *      and a reader without JavaScript gets a search field that silently eats their typing —
 *      measured in the built page, not assumed. The gate renders a same-height placeholder first,
 *      so gaining the real input on hydration costs no layout shift. Filtering is pure
 *      enhancement: with no JS the page is simply complete and unfiltered, which is the correct
 *      degradation for content that is all present anyway.
 */

/** Elements the page marks for us. Kept as data attributes rather than classes or ids so a design
 *  change to /faq cannot quietly unhook the search. */
const SEL = {
  entry: "[data-faq-entry]",
  tutorial: "[data-faq-tutorial]",
  category: "[data-faq-category]",
  tocLink: "[data-toc-for]",
  tocGroup: "[data-toc-group]",
  section: "[data-faq-section]",
} as const;

type Indexed = { el: HTMLElement; text: string; id: string };

/** Lowercase, strip accents, collapse whitespace. Done once at mount for every node, and once per
 *  keystroke for the query, so "stoploss" and "Stop-Loss" meet in the middle. */
const norm = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .trim();

export type SearchLabels = {
  placeholder: string;
  aria: string;
  hintPre: string;
  hintMid: string;
  hintPost: string;
  none: string;
  shown: string;
  clear: string;
};

/** Labels are passed IN, not imported: this is a client component, and shipping the whole UI
 *  dictionary to the browser to read eight strings would be waste. */
export function FaqSearch({
  total,
  labels,
}: {
  total: number;
  labels: SearchLabels;
}) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<number | null>(null);
  /** False during the server render and the first client paint — see note 3 in the header. */
  const [mounted, setMounted] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const idx = useRef<{
    entries: Indexed[];
    tutorials: Indexed[];
    categories: HTMLElement[];
    tocLinks: { el: HTMLElement; for: string }[];
    tocGroups: HTMLElement[];
    sections: HTMLElement[];
  } | null>(null);

  /** Build the index once, from what the server rendered. */
  useEffect(() => {
    const grab = (sel: string, attr: string): Indexed[] =>
      Array.from(document.querySelectorAll<HTMLElement>(sel)).map((el) => ({
        el,
        id: el.getAttribute(attr) ?? "",
        // textContent, not innerText: it does not force a layout pass and it reads text inside
        // collapsed or offscreen nodes, which is what we want for matching.
        text: norm(el.textContent ?? ""),
      }));

    idx.current = {
      entries: grab(SEL.entry, "data-faq-entry"),
      tutorials: grab(SEL.tutorial, "data-faq-tutorial"),
      categories: Array.from(
        document.querySelectorAll<HTMLElement>(SEL.category),
      ),
      tocLinks: Array.from(
        document.querySelectorAll<HTMLElement>(SEL.tocLink),
      ).map((el) => ({ el, for: el.getAttribute("data-toc-for") ?? "" })),
      tocGroups: Array.from(
        document.querySelectorAll<HTMLElement>(SEL.tocGroup),
      ),
      sections: Array.from(document.querySelectorAll<HTMLElement>(SEL.section)),
    };
    setMounted(true);
  }, []);

  const apply = useCallback((raw: string) => {
    const i = idx.current;
    if (!i) return;

    const tokens = norm(raw).split(" ").filter(Boolean);

    // Empty query restores the page exactly — every toggle this component owns is cleared, so the
    // document goes back to the server-rendered state rather than to "whatever was last visible".
    if (tokens.length === 0) {
      for (const g of [i.entries, i.tutorials])
        for (const x of g) x.el.hidden = false;
      for (const el of [...i.categories, ...i.tocGroups, ...i.sections])
        el.hidden = false;
      for (const l of i.tocLinks) l.el.hidden = false;
      setHits(null);
      return;
    }

    // AND across tokens: "binance key" should find the answer containing both words, not the union
    // of everything mentioning either. Substring rather than word-boundary matching so a partial
    // word still narrows as you type ("with" → "withdrawal").
    const match = (t: string) => tokens.every((tok) => t.includes(tok));

    const visible = new Set<string>();
    let n = 0;
    for (const g of [i.entries, i.tutorials]) {
      for (const x of g) {
        const ok = match(x.text);
        x.el.hidden = !ok;
        if (ok) {
          visible.add(x.id);
          n++;
        }
      }
    }

    // A category heading with nothing under it is noise, so hide the wrapper once all of its
    // entries are hidden. Same for the two top-level sections and for the contents list.
    for (const cat of i.categories) {
      cat.hidden = !Array.from(
        cat.querySelectorAll<HTMLElement>(SEL.entry),
      ).some((el) => !el.hidden);
    }
    for (const l of i.tocLinks) l.el.hidden = !visible.has(l.for);
    for (const g of i.tocGroups) {
      g.hidden = !Array.from(g.querySelectorAll<HTMLElement>(SEL.tocLink)).some(
        (el) => !el.hidden,
      );
    }
    for (const s of i.sections) {
      const kids = s.querySelectorAll<HTMLElement>(
        `${SEL.entry},${SEL.tutorial}`,
      );
      s.hidden = kids.length > 0 && !Array.from(kids).some((el) => !el.hidden);
    }

    setHits(n);
  }, []);

  const onChange = (v: string) => {
    setQ(v);
    apply(v);
  };

  /** "/" focuses the box, Escape clears it — the two shortcuts people try without being told. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing =
        el?.tagName === "INPUT" ||
        el?.tagName === "TEXTAREA" ||
        el?.isContentEditable;
      if (e.key === "/" && !typing) {
        e.preventDefault();
        inputRef.current?.focus();
      } else if (e.key === "Escape" && el === inputRef.current) {
        onChange("");
        inputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onChange is stable (apply is memoized)
  }, []);

  // Same height as the real control (input + status line), so hydration swaps them in place
  // instead of shoving the page down.
  if (!mounted) return <div className="mt-7 h-[62px]" aria-hidden />;

  return (
    <div className="mt-7">
      <div className="relative">
        <span
          aria-hidden
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted"
        >
          ⌕
        </span>
        <input
          ref={inputRef}
          type="search"
          value={q}
          onChange={(e) => onChange(e.target.value)}
          placeholder={labels.placeholder}
          aria-label={labels.aria}
          aria-describedby="faq-search-status"
          className="input w-full pl-9! pr-20!"
        />
        {q && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted hover:text-primary"
          >
            {labels.clear}
          </button>
        )}
      </div>

      {/* aria-live so a screen reader hears the result count change without moving focus. */}
      <p
        id="faq-search-status"
        aria-live="polite"
        className="mt-2 font-mono text-[11px] text-muted"
      >
        {hits === null ? (
          <>
            {labels.hintPre}{" "}
            <kbd className="chip px-1.5! py-0! font-mono">/</kbd>{" "}
            {labels.hintMid}{" "}
            <kbd className="chip px-1.5! py-0! font-mono">Esc</kbd>{" "}
            {labels.hintPost}
          </>
        ) : hits === 0 ? (
          <span className="text-warn">
            {labels.none} “{q}” — enfo@twoke.io
          </span>
        ) : (
          <>
            {hits} / {total} {labels.shown}
          </>
        )}
      </p>
    </div>
  );
}
